import type { AxiosError } from 'axios';

import {
  FEATURE_KEYS,
  type FeatureKey,
  QUOTA_KEYS,
  type QuotaKey,
} from '@/core/authz/entitlement-catalog';

function isFeatureKey(value: string): value is FeatureKey {
  return (FEATURE_KEYS as readonly string[]).includes(value);
}

function isQuotaKey(value: string): value is QuotaKey {
  return (QUOTA_KEYS as readonly string[]).includes(value);
}

export type UpgradeRequiredContext =
  | { type: 'feature'; feature: FeatureKey }
  | {
      type: 'quota';
      quota: QuotaKey;
      limit: number;
      current: number;
    }
  | {
      type: 'feedback_payg';
      quota: 'quota.feedback_invite_recipients';
      limit: number;
      current: number;
      additional: number;
    };

type UpgradeErrorBody =
  | { code: 'entitlement_feature_locked'; feature: FeatureKey }
  | {
      code: 'entitlement_quota_exceeded';
      quota: QuotaKey;
      limit: number;
      current: number;
    }
  | {
      code: 'feedback_payg_required';
      quota: 'quota.feedback_invite_recipients';
      limit: number;
      current: number;
      additional: number;
    };

function extractErrorBody(data: unknown): UpgradeErrorBody | null {
  if (typeof data !== 'object' || data === null) return null;

  const messageBody =
    'message' in data &&
    typeof (data as { message?: unknown }).message === 'object' &&
    (data as { message?: unknown }).message !== null
      ? (data as { message: unknown }).message
      : data;

  if (typeof messageBody !== 'object' || messageBody === null) return null;

  const code = (messageBody as { code?: string }).code;
  if (code === 'entitlement_feature_locked') {
    const feature = (messageBody as { feature?: string }).feature;
    if (typeof feature === 'string' && isFeatureKey(feature)) {
      return { code, feature };
    }
  }

  if (code === 'entitlement_quota_exceeded') {
    const quota = (messageBody as { quota?: string }).quota;
    const limit = (messageBody as { limit?: number }).limit;
    const current = (messageBody as { current?: number }).current;
    if (
      typeof quota === 'string' &&
      isQuotaKey(quota) &&
      typeof limit === 'number' &&
      typeof current === 'number'
    ) {
      return {
        code,
        quota,
        limit,
        current,
      };
    }
  }

  if (code === 'feedback_payg_required') {
    const limit = (messageBody as { limit?: number }).limit;
    const current = (messageBody as { current?: number }).current;
    const additional = (messageBody as { additional?: number }).additional;
    if (
      typeof limit === 'number' &&
      typeof current === 'number' &&
      typeof additional === 'number'
    ) {
      return {
        code,
        quota: 'quota.feedback_invite_recipients',
        limit,
        current,
        additional,
      };
    }
  }

  return null;
}

export function parseUpgradeRequiredError(
  error: unknown,
): UpgradeRequiredContext | null {
  const axiosError = error as AxiosError<unknown>;
  if (axiosError.response?.status !== 403) return null;

  const body = extractErrorBody(axiosError.response.data);
  if (!body) return null;

  if (body.code === 'entitlement_feature_locked') {
    return { type: 'feature', feature: body.feature };
  }

  if (body.code === 'feedback_payg_required') {
    return {
      type: 'feedback_payg',
      quota: body.quota,
      limit: body.limit,
      current: body.current,
      additional: body.additional,
    };
  }

  return {
    type: 'quota',
    quota: body.quota,
    limit: body.limit,
    current: body.current,
  };
}
